/* ==========================================================================
   pdp-bigtent.js
   BigTent PDP — interactive helpers (web components / facades).

   Scope: Loaded only by sections that need it (pdp-side-by-side rows with
   media_kind=video_youtube; pdp-shorts-rail). Sections include the script
   via {{ 'pdp-bigtent.js' | asset_url | script_tag }}. Multiple inclusions
   are idempotent (custom element guards prevent re-registration).
   ========================================================================== */
(function () {
  'use strict';

  // --------------------------------------------------------------------------
  // <pdp-youtube-facade data-youtube-id="...">
  //
  // Renders a clickable thumbnail (provided as children) and, on first
  // interaction, swaps in a youtube-nocookie iframe. Privacy-aware (no YT
  // request on initial page load) and saves bandwidth on cards never opened.
  // --------------------------------------------------------------------------
  if (!customElements.get('pdp-youtube-facade')) {
    class PdpYouTubeFacade extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        this.addEventListener('click', this._handleClick);
        this.addEventListener('keydown', this._handleKey);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._handleClick);
        this.removeEventListener('keydown', this._handleKey);
      }

      _handleClick = (event) => {
        if (this._activated) return;
        event.preventDefault();
        this._activate();
      };

      _handleKey = (event) => {
        if (this._activated) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this._activate();
        }
      };

      _activate() {
        const id = this.dataset.youtubeId;
        if (!id) return;
        this._activated = true;

        const iframe = document.createElement('iframe');
        const safeId = encodeURIComponent(id);
        iframe.src =
          'https://www.youtube-nocookie.com/embed/' +
          safeId +
          '?autoplay=1&modestbranding=1&playsinline=1&rel=0';
        iframe.title = this.getAttribute('aria-label') || 'YouTube video';
        iframe.allow =
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.setAttribute('allowfullscreen', '');
        iframe.loading = 'lazy';
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.border = '0';
        iframe.style.display = 'block';

        this.replaceChildren(iframe);
      }
    }

    customElements.define('pdp-youtube-facade', PdpYouTubeFacade);
  }

  // --------------------------------------------------------------------------
  // <pdp-instagram-facade data-shortcode="...">
  //
  // Same click-to-load facade as YouTube, but swaps in an Instagram Reel embed
  // iframe. Uses the unofficial /reel/{shortcode}/embed/ endpoint (Instagram
  // exposes no public iframe API) — re-verify it still renders if IG changes
  // it. No Instagram request until the user clicks (privacy + perf). The embed
  // can't autoplay, so playing the reel needs a second click on its own play
  // button; Instagram fires no ended event, so we can't restore the thumbnail.
  // --------------------------------------------------------------------------
  if (!customElements.get('pdp-instagram-facade')) {
    class PdpInstagramFacade extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        this.addEventListener('click', this._handleClick);
        this.addEventListener('keydown', this._handleKey);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._handleClick);
        this.removeEventListener('keydown', this._handleKey);
      }

      _handleClick = (event) => {
        if (this._activated) return;
        event.preventDefault();
        this._activate();
      };

      _handleKey = (event) => {
        if (this._activated) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this._activate();
        }
      };

      _activate() {
        const shortcode = this.dataset.shortcode;
        if (!shortcode) return;
        this._activated = true;

        const iframe = document.createElement('iframe');
        const safeCode = encodeURIComponent(shortcode);
        iframe.src =
          'https://www.instagram.com/reel/' + safeCode + '/embed/';
        iframe.title = this.getAttribute('aria-label') || 'Instagram reel';
        iframe.allow =
          'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('scrolling', 'no');
        iframe.loading = 'lazy';
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.border = '0';
        iframe.style.display = 'block';
        iframe.style.background = '#fff';

        this.replaceChildren(iframe);
      }
    }

    customElements.define('pdp-instagram-facade', PdpInstagramFacade);
  }

  // --------------------------------------------------------------------------
  // <pdp-video-facade>  (child: thumbnail <button> + inert <template><video>)
  //
  // Shopify-hosted video (mp4/webm). Same click-to-load facade, but the file is
  // same-origin so there's no privacy concern — the facade just avoids
  // downloading the video until the user clicks. On click the <video> is cloned
  // from the inert <template> and played; the click is a user gesture, so it
  // plays with sound. 1 click to play.
  // --------------------------------------------------------------------------
  if (!customElements.get('pdp-video-facade')) {
    class PdpVideoFacade extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        this.addEventListener('click', this._handleClick);
        this.addEventListener('keydown', this._handleKey);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._handleClick);
        this.removeEventListener('keydown', this._handleKey);
      }

      _handleClick = (event) => {
        if (this._activated) return;
        event.preventDefault();
        this._activate();
      };

      _handleKey = (event) => {
        if (this._activated) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this._activate();
        }
      };

      _activate() {
        if (this._activated) return;
        const tpl = this.querySelector('template');
        if (!tpl) return;
        this._activated = true;

        const frag = tpl.content.cloneNode(true);
        const video = frag.querySelector('video');
        this.replaceChildren(frag);
        if (video) {
          const played = video.play();
          if (played && played.catch) {
            played.catch(function () {});
          }
        }
      }
    }

    customElements.define('pdp-video-facade', PdpVideoFacade);
  }
})();
